/**
 * Module-level presence tracker for people search results.
 * Equivalent to legacy ChatPeopleStatusService.js: `api/project/Search` returns
 * `isOnLine` computed once per request, so it goes stale the moment someone
 * connects/disconnects after the search fired. This overlay is kept in sync via
 * socket events (see socketService.ts) and applied on top of fresh search results.
 */

interface PersonOnlineStatus {
  whoID: number;
  isOnLine: boolean;
}

const peopleOnlineList: PersonOnlineStatus[] = [];

type PeopleOnLineListener = () => void;
const listeners = new Set<PeopleOnLineListener>();

/**
 * Subscribe to presence updates so already-rendered results (e.g. the search
 * autocomplete) can re-apply `getPeopleOnLine` when a socket event arrives
 * after the initial fetch, instead of staying stuck with a stale snapshot.
 */
export function subscribeToPeopleOnLineChanges(listener: PeopleOnLineListener): () => void
{
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners(): void
{
  listeners.forEach(listener => listener());
}

/**
 * Connected person IDs (from `socket:client:allConnectedUsersWeb`). Matches legacy
 * usersReducer's UPDATE_USER_CONNECTION_STATUS: additive, only ever flips people
 * in the list to online — it never marks anyone else offline (that's
 * `listUserOnLine`'s job) and must not wipe previously tracked statuses.
 */
export function updatePeopleOnLine(personIds: number[]): void
{
  personIds.forEach(id =>
  {
    const existing = peopleOnlineList.find(p => p.whoID === id);
    if (existing)
    {
      existing.isOnLine = true;
    }
    else
    {
      peopleOnlineList.push({ whoID: id, isOnLine: true });
    }
  });
  notifyListeners();
}

/** Single person's status change (from `socket:client:listUserOnLine`). */
export function findAndUpdatePeopleOnLine(data: { whoID: number; isOnLine: boolean }): void
{
  const existing = peopleOnlineList.find(p => p.whoID === data.whoID);
  if (existing)
  {
    existing.isOnLine = data.isOnLine;
  }
  else
  {
    peopleOnlineList.push({ whoID: data.whoID, isOnLine: data.isOnLine });
  }
  notifyListeners();
}

/**
 * Overlays live presence onto search results, matched by `ID`.
 * Items with no tracked presence are returned unchanged (the backend's own
 * `isOnLine` — usually `false`, see class comment — is left as-is).
 */
export function getPeopleOnLine<T extends { ID: number; isOnLine: boolean }>(items: T[]): T[]
{
  if (peopleOnlineList.length === 0) return items;

  return items.map(item =>
  {
    const found = peopleOnlineList.find(p => p.whoID === item.ID);
    return found ? { ...item, isOnLine: found.isOnLine } : item;
  });
}
